import type { Player } from "@golazo/shared";
import { len2 } from "../core/math.js";

/**
 * El nivel de la IA en la CARRERA (`Player.aiSkill`).
 *
 * Medido en produccion el 2026-09-26: contra la IA de siempre, la gente nueva
 * perdia 9 de cada 10 partidos de 3v3, con un 2-7 de media. Y la carrera
 * empieza justo ahi, en la Liga del Barrio. Asi que la IA de la carrera tiene
 * niveles: en el barrio es torpe de verdad (piensa tarde, pasa y chuta peor,
 * remata de primera a destiempo, el portero se tira tarde, elige peor) y va
 * afinando division a division hasta la de siempre en Primera.
 *
 * `aiSkill` va de 0 (barrio) a 1 (la de siempre). Por debajo de 0 se admite
 * hasta -3: no lo usa la carrera, lo usa la sonda (`pnpm barrio:probe`) para
 * imitar a alguien que acaba de coger el mando (que, medido, juega bastante
 * peor que la IA mas torpe).
 *
 * Sin `aiSkill` todo multiplica por 1 exacto y el partido es el de siempre
 * bit a bit: el online no lo pone nunca (lo vigila la paridad del motor).
 */
export const AI_SKILL = {
  /** Cada cuanto piensa: en 0, un 140 % mas lento. */
  REACTION: 1.4,
  /** Error de pase y de tiro: en 0, casi el triple. */
  AIM: 1.8,
  /** Error de tiempo al rematar de primera. */
  FIRST_TIME: 2.5,
  /** Lo que tarda el portero en reaccionar a un tiro. */
  GK_REACTION: 1.2,
  /**
   * Cuanto menos corre en el barrio. Medido con `pnpm barrio:probe`: en 3v3
   * los goles salen de quien llega antes al balon, no de la punteria, y sin
   * esto un rival torpe seguia metiendo casi los mismos goles.
   *
   * Era 0,25 (y lineal con el nivel). Desde que el torpe tambien DECIDE peor
   * (abajo) basta con 0,13, y crece con la RAIZ de la torpeza: en el barrio,
   * donde las decisiones pesan mas, las piernas se recortan casi a la mitad;
   * en Segunda (0,6) apenas, porque ahi las decisiones casi no restan. Con
   * menos, el novato del barrio perdia mas que antes (PACE=0: gana el 30 %
   * en vez del 48 %).
   */
  PACE: 0.13,

  /*
   * DECISIONES (ver `decisionSlack`). Lo de arriba hace que el torpe EJECUTE
   * peor; esto, que ELIJA peor, que es lo que separa a un jugador malo en
   * Football Manager (atributos "anticipacion", "vision", "decisiones").
   */
  /** Cuanto pesan todas a la vez: 0 las apaga (para comparar en las sondas). */
  DECISIONS: 1,
  /**
   * Anticipacion: cuanto de la trayectoria del balon suelto adivina. En el
   * barrio solo un 30 %: corre hacia donde ESTA el balon, no hacia donde va a
   * estar, y llega tarde y en curva. Es la que mas pesa (`pnpm
   * decision:probe`, 3v3, nivel 0): sin ella el torpe encaja 3,7 goles por
   * partido contra la de siempre; con ella, 4,6.
   */
  ANTICIPATION: 0.7,
  /**
   * Vision: a esta distancia (dividida por la torpeza) deja de ver a un
   * compañero. En el barrio, 380: no ve el cambio de juego ni al que llega por
   * el otro lado. Junto con quitarle el pase al hueco y la pared (en
   * `evaluatePass`), juega corto: el pase medio baja de 359 a 282 unidades.
   */
  VISION_RANGE: 380,
  /**
   * Tampoco ve al que tiene a la espalda (el coseno con hacia donde mira por
   * debajo de esto), salvo que este pegado. Solo por encima de esta torpeza:
   * del barrio a Regional.
   */
  VISION_BACK_COS: -0.2,
  VISION_BACK_NEAR: 150,
  VISION_BACK_SLACK: 0.5,
  /**
   * El carril: mira al compañero, no al rival que se mete en medio. Cuanto
   * del hueco que de verdad hay no ve (0 lo ve todo, 1 nada): un carril tapado
   * y uno limpio le parecen parecidos, y acaba dando pases que se lleva el que
   * estaba en la linea. Se castiga poniendose en medio.
   *
   * Pesa poco: en 3v3 solo tiene un compañero de campo, asi que no cambia A
   * QUIEN pasa; en 5v5 sube los pases robados uno o dos puntos.
   */
  LANE_BLINDNESS: 0.6,
};

/** 0 = la de siempre; 1 = barrio; hasta 4 = alguien que acaba de coger el mando. */
function clumsiness(player: Player): number {
  if (player.aiSkill === undefined) return 0;
  return 1 - Math.max(-3, Math.min(1, player.aiSkill));
}

export function reactionScale(player: Player): number {
  return 1 + clumsiness(player) * AI_SKILL.REACTION;
}

export function aimScale(player: Player): number {
  return 1 + clumsiness(player) * AI_SKILL.AIM;
}

export function firstTimeScale(player: Player): number {
  return 1 + clumsiness(player) * AI_SKILL.FIRST_TIME;
}

/**
 * Solo para los asientos de IA con nivel entre 0 y 1: el "novato" de la sonda
 * no corre menos. Con la raiz: barrio -13 %, Regional -11 %, Segunda -8 %.
 */
export function paceScale(player: Player): number {
  if (player.aiSkill === undefined) return 1;
  return 1 - Math.sqrt(Math.max(0, Math.min(1, 1 - player.aiSkill))) * AI_SKILL.PACE;
}

export function keeperReactionScale(player: Player): number {
  return 1 + clumsiness(player) * AI_SKILL.GK_REACTION;
}

/**
 * Cuanto usa el cuerpo (embestir, plantarse y aguantar de espaldas, ver
 * `AI.BODY_CHECK_CHANCE`): 1 la de siempre, 0 el barrio y por debajo. Es un
 * gesto de jugador hecho: ni el rival del barrio ni el novato de la sonda lo
 * usan.
 *
 * Crece con el CUADRADO del nivel (Regional 0,09, Segunda 0,36, Primera 1):
 * en lineal, el novato de `pnpm barrio:probe` contra Regional —la dificultad
 * "media"— pasaba de ganar el 34 % a el 28 %.
 */
export function bodyScale(player: Player): number {
  if (player.aiSkill === undefined) return 1;
  const s = Math.max(0, Math.min(1, player.aiSkill));
  return s * s;
}

/**
 * Cuanto lee el juego para RECICLAR (`AI.RECYCLE_*`): 1 la de siempre, 0 el
 * barrio y por debajo, en lineal. Es una decision, no un gesto fisico: en
 * Football Manager la separa el atributo "decisiones". Con esto el novato de
 * `pnpm barrio:probe` —la misma IA con nivel bajo— no recicla, que es lo que
 * haria alguien que acaba de coger el mando.
 */
export function readScale(player: Player): number {
  if (player.aiSkill === undefined) return 1;
  return Math.max(0, Math.min(1, player.aiSkill));
}

/**
 * Cuanto PEOR elige (0 la de siempre, 1 el barrio y por debajo): lo que mueven
 * `AI_SKILL.ANTICIPATION`, `VISION_*` y `LANE_BLINDNESS`.
 *
 * La dificultad estaba en las piernas: el del barrio pensaba tarde, fallaba y
 * corria menos, y eso se lee como un robot lento, no como un mal futbolista.
 * Un mal futbolista corre igual; lo que hace es llegar tarde al balon suelto
 * porque va a donde esta, no ver al compañero que le queda lejos o a la
 * espalda, y meter el pase por donde hay un rival. Son errores que se ven y se
 * castigan: ponerte en la linea de pase o ganarle la carrera al rechace.
 *
 * El asiento de la persona (el novato de `pnpm barrio:probe`) no la lleva: tus
 * decisiones no las cambia este codigo, y si el novato tambien eligiera peor
 * la sonda mediria otra persona en cada tanda.
 */
export function decisionSlack(player: Player): number {
  if (player.aiSkill === undefined || player.human) return 0;
  return (1 - Math.max(0, Math.min(1, player.aiSkill))) * AI_SKILL.DECISIONS;
}

/** Parte de la trayectoria del balon suelto que adivina (1 = toda). */
export function anticipationScale(player: Player): number {
  return 1 - decisionSlack(player) * AI_SKILL.ANTICIPATION;
}

/**
 * ¿Ve a este compañero para pasarsela? A la persona se la ve siempre: en el
 * barrio eres el que destaca, y un compañero que nunca te la da es un partido
 * que no se juega.
 */
export function seesMate(player: Player, mate: Player, slack: number): boolean {
  if (slack <= 0 || mate.human) return true;
  const dx = mate.pos.x - player.pos.x;
  const dy = mate.pos.y - player.pos.y;
  const d = len2(dx, dy);
  if (d * slack > AI_SKILL.VISION_RANGE) return false;
  if (slack < AI_SKILL.VISION_BACK_SLACK || d <= AI_SKILL.VISION_BACK_NEAR) return true;
  return (dx * player.facing.x + dy * player.facing.y) / d >= AI_SKILL.VISION_BACK_COS;
}
